import * as React from 'react';
import { ViewProp } from '..';
import { PageIntro } from '../pageintro';
import { Payment } from './payment';
import { UserInfo } from './userInfo';
import * as mrCap from '../../mrCapTypes';
import format from 'date-fns/format';
import getDay from 'date-fns/getDay';
import addDays from 'date-fns/addDays';
import sv from 'date-fns/locale/sv';
import * as gtm from '../../gtm';
import { autobind, className, formatNumber, getCampaignId, setCampaignCode } from '../../util';
import { globalStrings } from '../index';
import { Input } from './input';
import { getProducts } from '../../api/booking';

interface Props extends ViewProp {
}

interface State {
  campaignCode: string;
  invalidCampaign: boolean;
}

export class Confirm extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);

    this.state = {
      campaignCode: getCampaignId(),
      invalidCampaign: false
    };
  }

  // Need these variables in order to hide Ecster, hide payment toggling and show
  // correct fields in the form.
  private ProductsTotalPrice: number = this.props.selectedProducts.reduce((total, item) => (
    item.salePriceVehicle
      ? item.salePriceVehicle * item.noi
      : item.priceVehicle * item.noi)
    + total, 0);

  private RentalTotalPrice: number | undefined = this.props.mabi && this.props.selectedMabi
    ? this.props.mabi.vehicles.find(vehicle => vehicle.artNr === this.props.selectedMabi)?.price : 0;

  private TotalPrice: number = this.ProductsTotalPrice + (this.RentalTotalPrice ?? 0);

  componentDidMount() {
    this.props.setBookingDetails({
      ...this.props.booking,
      ...{
        bookingType: this.props.financing
          ? this.TotalPrice > 0 ? mrCap.BookingType.ecsterPay : mrCap.BookingType.onLocation
          : mrCap.BookingType.onLocation
      }
    });

    gtm.checkout(this.props.selectedProducts
      .map(product => gtm.convertProduct(product, this.props.campaign?.code)));
  }

  toggleFinancing = () => {
    return;

    this.props.togglePayment();

    this.props.setBookingDetails({
      ...this.props.booking,
      ...{
        bookingType: this.props.financing
          ? mrCap.BookingType.onLocation
          : mrCap.BookingType.ecsterPay
      }
    });
  };

  @autobind
  async useCampaignCode() {
    setCampaignCode(this.state.campaignCode);

    const productsResponse = await getProducts(this.props.selectedShop!.id, this.props.regNr);

    this.props.updateSelectedProducts(productsResponse.products);

    if (productsResponse.campaign) {
      this.props.setCampaign(productsResponse.campaign);
    }

    this.setState({ invalidCampaign: !!!productsResponse.campaign });
  }

  render() {
    const onlyProducts = this.props.selectedProducts.every(item => item.type === mrCap.ArticleType.product);
    const shop = this.props.selectedShop!;
    const date = this.props.selectedDate!;
    const products = this.props.selectedProducts!;
    const showToggle = !onlyProducts && this.props.financingTypes.length > 1;

    let totalPrice = products.reduce((total, item) => (item.priceVehicle * item.noi) + total, 0);

    let totalDiscountPrice = products.reduce((total, item) => (
      item.salePriceVehicle ? item.salePriceVehicle * item.noi : item.priceVehicle * item.noi)
      + total, 0);

    const rental = !!this.props.selectedMabi;
    const rentalVehicle = rental
      ? this.props.mabi?.vehicles.find(vehicle => vehicle.artNr === this.props.selectedMabi)
      : null;

    const selectedDate = this.props.selectedDate!;
    // Checks if theres any products that has to be picked up the day after
    const dayAfterProducts = this.props.selectedProducts.some(item => item.pickUpDayAfter === true);

    // Checks if the date is a friday
    const pickUpFriday = selectedDate && getDay(selectedDate) === 5;

    // If the date is a friday, add three full days, otherwise add one
    const pickUpDay = pickUpFriday ? addDays(selectedDate, 3) : addDays(selectedDate, 1);

    if (rentalVehicle) {
      const pickup = pickUpFriday ? rentalVehicle.price * 3 : rentalVehicle.price;
      totalPrice += pickup;
      totalDiscountPrice += pickup;
    }

    return (
      <div className='outer-page'>
        <div className='page'>

          <div className='page-top-content'>
            <PageIntro
              header={this.props.texts.stepThreeHeader}
              preamble={this.props.texts.stepThreeParagraph} />
          </div>

          <div className='page-split'>
            <div className='page-split-col left-col'>

              <div className='summary-list'>
                {!onlyProducts && shop.name != null &&
                  <div className='item' key={'shop-name'}><strong>Anläggning: </strong>{shop.name}</div>}

                {!onlyProducts && date != null &&
                  <div className='item' key={'selected-date'}>
                    <strong>Datum: </strong>{format(date, 'PPPP', {locale: sv}).toString()}</div>}

                {!onlyProducts &&
                  <div className='item' key={'message'}>
                    <strong>Tid för inlämning: </strong>{this.props.texts.stepThreeSummaryMessage}</div>}

                {!onlyProducts && dayAfterProducts && pickUpDay && <>
                  <div className='item' key={'pick-up'}>
                    <strong><i className='fas fa-exclamation-circle'></i> Tid för tidigast upphämtning: </strong>
                    {format(pickUpDay, 'dd MMMM', {locale: sv})} från 08.00</div>
                </>}

                {this.props.campaign &&
                  <div className='item' key={'campaign'}>
                    <strong>Kampanj: </strong>
                    {this.props.campaign.name} {this.props.campaign.discount > 0
                      ? `(${this.props.campaign.discount} %)`
                      : null}
                  </div>}

                <div className='item' key={'products'}>
                  {products.length > 1
                    ? <>
                      <strong>Valda produkter: </strong>
                      {products.map(item =>
                        <ListProductItem
                          name={item.title}
                          price={item.priceVehicle}
                          salePrice={item.salePriceVehicle}
                          amount={item.noi} />)}
                    </>:<>
                      <strong>Vald produkt: </strong>
                      {products.map(item =>
                        <ListProductItem
                          name={item.title}
                          price={item.priceVehicle}
                          salePrice={item.salePriceVehicle}
                          amount={item.noi} />)}
                    </>}
                  {this.props.shipping > 0 &&
                    <ListProductItem
                      name={'Fraktavgift'}
                      price={this.props.shipping}
                      icon={'shipping-fast'}
                      amount={1} />}
                </div>

                {!onlyProducts && this.props.regNr && this.props.carDetails && <>
                  <div className='item' key={'car'}>
                    <strong>Din bil: </strong>
                    <span>
                      {this.props.regNr + ', ' + this.props.carDetails.brand + ' ' + this.props.carDetails.model}
                    </span>
                  </div>
                </>}

                {rental && rentalVehicle && <>
                  <div className='item'>
                    <strong>Vald hyrbil: </strong>
                    <ListProductItem
                      name={pickUpFriday
                        ? rentalVehicle.name + ` (${rentalVehicle.price} SEK/dygn)`
                        : rentalVehicle.name}
                      price={pickUpFriday
                        ? rentalVehicle.price * 3
                        : rentalVehicle.price}
                      amount={1} />

                    {pickUpFriday && <>
                      <div className='mabi-friday-notice' key={'pick-up'}>
                        <div className='friday-message'
                          dangerouslySetInnerHTML={{__html: this.props.texts.stepTwoRentalMessage}}></div>
                        <span>
                          Tid för tidigast återlämning: {format(pickUpDay, 'dd MMMM', {locale: sv})} från 08.00
                        </span>
                      </div></>}

                  </div>
                </>}
              </div>
            </div>

            <div className='page-split-col right-col'>
              <UserInfo
                regNr={this.props.regNr}
                gdprText={this.props.texts.stepThreeGdpr}
                newsletterText={this.props.texts.stepThreeNewsletter}
                financing={this.TotalPrice > 0 ? this.props.financing : false}
                onlyProducts={onlyProducts}
                rental={rental}
                setBookingValues={
                  (e) => this.props.setBookingDetails({...this.props.booking, ...e})} />

              <div className='campaign-code-container'>
                <div className='campaign-row'>
                  <Input
                    value={this.state.campaignCode}
                    change={val => this.setState({ campaignCode: val })}
                    placeholder='Kampanjkod' />

                  <button className='cta-btn primary' onClick={e => {
                    e.preventDefault();
                    this.useCampaignCode();
                  }}>Använd kampanjkod</button>
                </div>

                {this.state.invalidCampaign ? <div className='campaign-row invalid-campaign'>
                  Ogiltig kampanjkod
                </div> : this.props.campaign && <div className='campaign-row'>
                  Kampanj: {this.props.campaign.name} ({this.props.campaign.discount}%)
                </div>}
              </div>

              <div className='payment-window'>

                {showToggle && this.TotalPrice > 0 ? <>
                  <div className='payment-toggler'
                    onClick={() => this.toggleFinancing()}>
                    <div className={className('payment-alternative payment-financing', {active: this.props.financing})}>
                      <span><i className='fas fa-credit-card'></i>Betala i förväg</span>
                    </div>
                    <div className={className('payment-alternative payment-on-site', {inactive: this.props.financing})}>
                      <span><i className='fas fa-warehouse'></i>Betala på plats</span>
                    </div>
                    <div className={className('payment-toggler-background', {toggled: !this.props.financing})}></div>
                  </div>
                </> : this.TotalPrice === 0 && <>
                  <div className='payment-toggler'>
                    <div className={'payment-alternative payment-on-site fullwidth'}>
                      <span><i className='fas fa-warehouse'></i>{globalStrings.zeroPrice}</span>
                    </div>
                    <div className={'payment-toggler-background fullwidth'}></div>
                  </div>
                </>}

                <Payment
                  bookingId={this.props.bookingId}
                  paymentSuccess={() => this.props.moveNext()}
                  paymentOnSiteText={this.props.texts.stepThreePaymentOnSite}
                  totalPrice={totalPrice}
                  totalDiscountPrice={totalDiscountPrice === totalPrice ? undefined : totalDiscountPrice}
                  ecsterConfig={this.props.ecster}
                  financing={this.TotalPrice > 0 ? this.props.financing : false}
                  booking={this.props.booking}
                  setBookingOk={(e) => this.props.setOk(e)}
                  onlyProducts={onlyProducts} />

              </div>
            </div>
          </div>

        </div>
      </div>
    );
  }
}

interface ListProductItemProps {
  name: string;
  price: number;
  salePrice?: number;
  icon?: string;
  amount: number;
}

function ListProductItem(props: ListProductItemProps) {
  let productName = props.name;

  if (productName.includes('Presentkort')) {
    productName = 'Presentkort';
  }

  const icon = props.icon ?? 'check';

  const getPrice = () => {
    if (props.price === 0) {
      return ' - ' + globalStrings.zeroPrice;
    }

    if (props.salePrice != null) {
      return <>
        {' - '}
        <span>
          {formatNumber(props.salePrice * props.amount)}
          {' '}
          <del>{formatNumber(props.price * props.amount)}</del> SEK
        </span>
      </>;
    }

    return <>
      {' - '} <span>{formatNumber(props.price * props.amount)}SEK</span>
    </>;
  };

  return(
    <span className='listedproduct'>
      <i className={'fas fa-'+icon}></i>
      {props.amount > 1 ? `${props.amount}st ` + productName : productName}
      <span className='confirm-price'>
        {getPrice()}

        {props.amount > 1
          ? <span className='standalone-price'>{' (' + props.price + ' SEK/st)'}</span>
          : null}
      </span>
    </span>
  );
}